<script setup lang="ts">
import { Fragment, type Emoji, emojiConfigQueryOptions } from '#shared'
import { useQuery } from '@tanstack/vue-query'
import { capitalize, computed, ref, toRef, useTemplateRef, watch } from 'vue'
import { useClickOutside } from '#shared/lib/misc/use-click-outside'
import { useEmojiRenderPlanner } from '../lib/use-emoji-render-planner'
import { useSearch } from '../lib/use-search'
import useUiConfigPreset from '../lib/use-ui-config-preset'
import EmojiRow from './emoji-row.vue'
import EmojiSearchBar from './emoji-search-bar.vue'
import EmojiSelectHeader from './emoji-select-header.vue'

interface StaticEmojiSelectProps {
  targetEl: HTMLElement | null
  size?: 'small' | 'medium' | 'large'
}

const props = defineProps<StaticEmojiSelectProps>()

const emit = defineEmits<{
  close: []
  select: [emoji: Emoji]
}>()

const size = toRef(() => props.size ?? 'medium')

const rootEl = useTemplateRef('rootEl')
useClickOutside(
  computed(() => [props.targetEl, rootEl.value]),
  () => emit('close')
)

const emojiConfigQuery = useQuery(emojiConfigQueryOptions)
const emojiConfig = computed(() => emojiConfigQuery.data.value ?? [])

const searchString = ref('')
const shownEmojis = useSearch(emojiConfig, searchString)

const listWrapperEl = useTemplateRef('listWrapperEl')
const scrollTop = ref(0)
const updateScrollTop = () => {
  scrollTop.value = listWrapperEl.value?.scrollTop ?? 0
}

watch(listWrapperEl, () => updateScrollTop())

const uiConfig = useUiConfigPreset(size)
const renderPlan = useEmojiRenderPlanner(shownEmojis, uiConfig, scrollTop)

const emojiListWidth = computed(
  () =>
    uiConfig.value.gridColumnCount * uiConfig.value.emojiSize +
    uiConfig.value.gridGap * (uiConfig.value.gridColumnCount - 1)
)

const emojiListHeight = computed(
  () =>
    uiConfig.value.gridRowCount * uiConfig.value.emojiSize + uiConfig.value.gridGap * (uiConfig.value.gridRowCount - 1)
)

const headerStyle = computed(() => ({
  width: `${emojiListWidth.value}px`,
}))

const listWrapperStyle = computed(() => ({
  width: `${emojiListWidth.value}px`,
  height: `${emojiListHeight.value}px`,
}))

const listStyle = computed(() => ({
  height: `${renderPlan.value.totalHeight}px`,
}))

const listContentStyle = computed(() => ({
  gridTemplateColumns: `repeat(${uiConfig.value.gridColumnCount}, 1fr)`,
  gap: `${uiConfig.value.gridGap}px`,
  transform: `translateY(${renderPlan.value.verticalOffset}px)`,
}))
</script>

<template>
  <div :class="[$style.root, $style['rootSize' + capitalize(size)]]" ref="rootEl">
    <div :class="$style.header" :style="headerStyle">
      <EmojiSearchBar v-model="searchString" />
    </div>

    <div :class="$style.listWrapper" :style="listWrapperStyle" ref="listWrapperEl" @scroll="updateScrollTop">
      <div :class="[$style.list, $style['listSize' + capitalize(size)]]" :style="listStyle">
        <div :class="$style.listContent" :style="listContentStyle">
          <Fragment v-for="item in renderPlan.items" :key="item.key">
            <EmojiRow v-if="item.type === 'emoji'" :emojis="item.emojis" @select="emit('select', $event)" />
            <EmojiSelectHeader v-if="item.type === 'header'" :text="item.text" />
          </Fragment>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '#mixins/scrollbar' as scrollbar;

.root {
  background-color: #303035;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #404045;
}

.header {
  margin-bottom: 10px;
}

.gap {
  grid-column: 1 / -1;
}

.listWrapper {
  overflow: auto;

  @include scrollbar.hide;
}

.list {
  position: relative;
}

.listContent {
  display: grid;
  width: 100%;
}
</style>
